/**
 * Standard async resource shape for list/detail slices.
 *
 * State: `{ data, loading, error }`
 * - **pending**: `loading = true`, `error = null`
 * - **fulfilled**: `loading = false`, `error = null`, data from handler
 * - **rejected**: `loading = false`, `error` = message (skipped for superseded GETs)
 *
 * ### Registration
 * Prefer `addAsyncResourceThunk(builder, thunk)` for a single fetch thunk per slice.
 * For composite slices (nested `loading`/`error`, `saving`/`saveError`, or extra keys), use
 * `completeResourceSuccess`, `applyResourceRejected`, `applySubResourceRejected`, or
 * `applySaveRejected` in `extraReducers` so behavior matches the helper above.
 *
 * ### Thunks
 * Use `getApiErrorMessage` in `catch` and `rejectWithValue` so `error` is a string.
 *
 * ### UI (pages)
 * 1. `if (loading)` → full-page or inline `<Loader />`
 * 2. `if (error && !loading)` → error message (use `useAsyncResource` when convenient)
 * 3. Render using `data`
 */

import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";

/** ApiBase aborts duplicate in-flight GETs; ignore so the active request can finish. */
export const SUPERSEDED_REQUEST = "Request canceled";

export function isSupersededRequestPayload(payload) {
  return payload === SUPERSEDED_REQUEST || isSilentRequestAbortError(payload);
}

/** After a successful read when you set `data` (or other fields) manually in `fulfilled`. */
export function completeResourceSuccess(state) {
  state.loading = false;
  state.error = null;
}

/**
 * Handle `rejected` for a flat `{ loading, error }` slice (or reducer state).
 * Skips updates when the request was superseded/canceled.
 */
export function applyResourceRejected(
  state,
  action,
  fallbackErrorMessage = "Something went wrong"
) {
  if (isSupersededRequestPayload(action.payload)) return;
  state.loading = false;
  state.error = action.payload ?? action.error?.message ?? fallbackErrorMessage;
}

/**
 * Same as `applyResourceRejected` for nested `{ loading, error }` (e.g. `state.preview`).
 */
export function applySubResourceRejected(
  target,
  action,
  fallbackErrorMessage = "Something went wrong"
) {
  if (isSupersededRequestPayload(action.payload)) return;
  target.loading = false;
  target.error = action.payload ?? action.error?.message ?? fallbackErrorMessage;
}

/**
 * For `{ saving, saveError }` mutation state (permissions matrix, vendor mappings, etc.).
 */
export function applySaveRejected(state, action, fallbackErrorMessage = "Something went wrong") {
  if (isSupersededRequestPayload(action.payload)) return;
  state.saving = false;
  state.saveError = action.payload ?? action.error?.message ?? fallbackErrorMessage;
}

/**
 * For slices with arbitrary `*Loading` / `*Error` keys (e.g. time entry summaries).
 */
export function applyBranchRejected(
  state,
  action,
  keys,
  fallbackErrorMessage = "Something went wrong"
) {
  if (isSupersededRequestPayload(action.payload)) return;
  state[keys.loadingKey] = false;
  state[keys.errorKey] = action.payload ?? action.error?.message ?? fallbackErrorMessage;
}

/**
 * Register pending / fulfilled / rejected for one async thunk on a resource slice.
 *
 * @param {import("@reduxjs/toolkit").ActionReducerMapBuilder<AsyncResourceState>} builder
 * @param {import("@reduxjs/toolkit").AsyncThunk<any, any, any>} thunk
 * @param {object} [options]
 * @param {(state: AsyncResourceState, action: import("@reduxjs/toolkit").PayloadAction<any>) => void} [options.onFulfilled] — default: `state.data = action.payload`
 * @param {(state: AsyncResourceState, action: import("@reduxjs/toolkit").PayloadAction<any>) => void} [options.onPending] — runs after `loading`/`error` reset (e.g. clear stale list)
 * @param {string} [options.fallbackErrorMessage]
 * @param {(payload: unknown) => boolean} [options.skipRejected] — return true to leave state unchanged
 */
export function addAsyncResourceThunk(builder, thunk, options = {}) {
  const {
    onFulfilled = (state, action) => {
      state.data = action.payload;
    },
    onPending,
    fallbackErrorMessage = "Something went wrong",
    skipRejected = isSupersededRequestPayload,
  } = options;

  builder
    .addCase(thunk.pending, (state, action) => {
      state.loading = true;
      state.error = null;
      if (onPending) onPending(state, action);
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      onFulfilled(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      if (skipRejected(action.payload)) return;
      state.loading = false;
      state.error = action.payload ?? action.error?.message ?? fallbackErrorMessage;
    });
}

/**
 * @typedef {{ data: unknown; loading: boolean; error: string | null }} AsyncResourceState
 */
